import React, { Fragment } from 'react';
import pluralize from 'pluralize';
import PropTypes from 'prop-types';
import { useQuery } from 'react-apollo';

import { getAsset } from '../../../../../../util/assets';
import Button from '../../../../../../components/global/Button';
import { formatPriceForDisplay } from '../../../../../../util/format-money';
import { getRawEventAddonItemsPrice } from '../../../FreemiumEventPurchase/helpers';
import GiftingEventAddonItemsPurchaseSelector from './components/GiftingEventAddonItemsPurchaseSelector';
import { AccountPromoCodeVerify } from '../../../../../../graphql/queries/account-referrals';
import { isIOSNativeAppWebView } from '../../../../../../services/native-bridge/native-apps-interface';
import { DISCOUNT_MODE } from '../../../../../../util/promo-codes';
import { CURRENCY } from '../../../../../../constants/currency';

const propTypes = {
  eventAddonItems: PropTypes.array.isRequired,
  selectedEventAddonItemIds: PropTypes.array.isRequired,
  onEventAddonItemToggle: PropTypes.func.isRequired,
  onPurchase: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  withHeader: PropTypes.bool,
  promoCode: PropTypes.string,
  possessiveCelebratedName: PropTypes.string.isRequired,
};

const PurchaseStep = ({
  eventAddonItems,
  selectedEventAddonItemIds,
  onEventAddonItemToggle,
  onPurchase,
  submitting,
  withHeader = true,
  promoCode = 'WALDO20',
  possessiveCelebratedName,
}) => {
  const isIOSNativeAppWebViewBool = isIOSNativeAppWebView();
  const checkoutPrice = getRawEventAddonItemsPrice({
    eventAddonItems: eventAddonItems.filter(({ id }) =>
      selectedEventAddonItemIds.includes(id),
    ),
  });

  const { data: { accountPromoCodeVerify: accountPromoCode } = {} } = useQuery(
    AccountPromoCodeVerify,
    {
      variables: {
        promoCode,
        // We don't care about price at this point, we only care about availability.
        price: { value: 0, currency: CURRENCY.USD },
      },
      fetchPolicy: 'network-only',
    },
  );

  return (
    <Fragment>
      {withHeader && (
        <div className="text-center">
          <img
            src={getAsset('/gift-colored-combo.svg')}
            width="60px"
            height="60px"
            className="mb-2"
            alt="Gift"
          />
          <h2 className="mb-1">{possessiveCelebratedName} Waldo wishlist</h2>
          <p className="text-light">
            Select the items you&apos;d like to buy as a gift for the couple!
          </p>
        </div>
      )}
      <GiftingEventAddonItemsPurchaseSelector
        selectedEventAddonItemIds={selectedEventAddonItemIds}
        onToggleEventAddonItem={eventAddonItem =>
          onEventAddonItemToggle(eventAddonItem.id)
        }
        eventAddonItems={eventAddonItems}
      />
      <div className="text-center mt-2">
        {accountPromoCode?.is_eligible && !isIOSNativeAppWebViewBool && (
          <p className="text-light text-sm text-italic text-center">
            Use{' '}
            <span className="text-primary text-extra-bold">{promoCode}</span>{' '}
            when you checkout for{' '}
            {accountPromoCode?.discount?.mode === DISCOUNT_MODE.AMOUNT
              ? `$${formatPriceForDisplay(
                  accountPromoCode.discount.value,
                  true,
                )}`
              : `${accountPromoCode.discount.value}%`}{' '}
            off!
          </p>
        )}
        {selectedEventAddonItemIds.length > 0 && (
          <Button
            data-testid="purchase-action"
            onClick={() => onPurchase()}
            disabled={submitting}
          >
            {submitting ? (
              'Processing...'
            ) : (
              <>
                {checkoutPrice
                  ? `Buy as ${pluralize(
                      'gift',
                      selectedEventAddonItemIds.length,
                    )} ($${formatPriceForDisplay(checkoutPrice)})`
                  : 'Select a gift'}
              </>
            )}
          </Button>
        )}
      </div>
    </Fragment>
  );
};

PurchaseStep.propTypes = propTypes;

export default PurchaseStep;
